import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { FiChevronDown, FiChevronUp, FiCalendar } from "react-icons/fi";
import "./policy.css";

const parseInlineFormatting = (text) => {
  if (!text || typeof text !== "string") return text;
  let cleaned = text.replace(/ -- /g, " – ").replace(/ --/g, " – ").replace(/-- /g, " – ");
  const parts = cleaned.split(/(\*\*[\s\S]*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const RenderFormattedContent = ({ content }) => {
  if (!content) return null;

  const rawBlocks = Array.isArray(content)
    ? content
    : typeof content === "string"
    ? content.replace(/\r\n/g, "\n").split(/\n\s*\n/)
    : [];

  return (
    <div className="policy-formatted-body">
      {rawBlocks.map((block, bIdx) => {
        if (!block || typeof block !== "string") return null;
        const trimmedBlock = block.trim();
        if (!trimmedBlock) return null;

        const rawLines = trimmedBlock.split("\n").map((l) => l.trim()).filter(Boolean);

        // Process bullet items
        if (rawLines.length > 0 && rawLines.some((line) => /^[-*•]\s+/.test(line))) {
          const items = [];
          let currentItem = "";

          rawLines.forEach((line) => {
            if (/^[-*•]\s+/.test(line)) {
              if (currentItem) items.push(currentItem);
              currentItem = line.replace(/^[-*•]\s+/, "");
            } else if (currentItem) {
              currentItem += " " + line;
            } else {
              currentItem = line;
            }
          });
          if (currentItem) items.push(currentItem);

          return (
            <ul key={bIdx} className="policy-bullet-list">
              {items.map((item, iIdx) => (
                <li key={iIdx} className="policy-bullet-item">
                  {parseInlineFormatting(item)}
                </li>
              ))}
            </ul>
          );
        }

        // Standard paragraph
        const textContent = rawLines.join(" ");
        return (
          <p key={bIdx} className="policy-paragraph">
            {parseInlineFormatting(textContent)}
          </p>
        );
      })}
    </div>
  );
};

const RenderListItems = ({ items }) => {
  if (!items || !Array.isArray(items) || items.length === 0) return null;

  return (
    <ul className="policy-bullet-list">
      {items.map((item, iIdx) => {
        if (typeof item !== "string") return null;
        const match = item.match(/^([A-Za-z0-9\s()&/–—'-]{2,45}):\s*(.*)$/);
        if (match) {
          return (
            <li key={iIdx} className="policy-bullet-item">
              <strong>{match[1]}:</strong> {parseInlineFormatting(match[2])}
            </li>
          );
        }
        return (
          <li key={iIdx} className="policy-bullet-item">
            {parseInlineFormatting(item)}
          </li>
        );
      })}
    </ul>
  );
};

const cleanDefinitionTerm = (term) => {
  if (!term) return "";
  if (term.includes('" or "')) {
    return term.split('" or "').map((t) => `“${t.replace(/"/g, "")}”`).join(" or ");
  }
  return `“${term.replace(/^"|"$/g, "")}”`;
};

const RenderDefinitions = ({ definitions }) => {
  if (!definitions || !Array.isArray(definitions) || definitions.length === 0) return null;

  return (
    <div className="policy-definitions-list">
      {definitions.map((def, idx) => (
        <div key={idx} className="policy-definition-item">
          <span className="policy-definition-term">{cleanDefinitionTerm(def.term)}</span>
          <span className="policy-definition-sep"> — </span>
          <span className="policy-definition-text">{parseInlineFormatting(def.definition)}</span>
        </div>
      ))}
    </div>
  );
};

const RenderGroups = ({ groups }) => {
  if (!groups || !Array.isArray(groups) || groups.length === 0) return null;

  return (
    <div className="policy-groups-container">
      {groups.map((group, gIdx) => (
        <div key={gIdx} className="policy-group-item">
          <h5 className="policy-group-title">
            ({group.id}) {group.title}
          </h5>
          {group.intro && (
            <p className="policy-paragraph">{parseInlineFormatting(group.intro)}</p>
          )}
          {group.content && (
            <RenderFormattedContent content={group.content} />
          )}
          {group.items && (
            <RenderListItems items={group.items} />
          )}
        </div>
      ))}
    </div>
  );
};

const RenderSubgroups = ({ subgroups }) => {
  if (!subgroups || !Array.isArray(subgroups) || subgroups.length === 0) return null;

  return (
    <div className="policy-subgroups-container">
      {subgroups.map((sg, sIdx) => (
        <div key={sIdx} className="policy-subgroup-item">
          <h5 className="policy-subgroup-title">{sg.title}</h5>
          {sg.items && <RenderListItems items={sg.items} />}
        </div>
      ))}
    </div>
  );
};

export default function PolicyLayout({ data }) {
  const [openAccordionId, setOpenAccordionId] = useState(1);
  const [activeId, setActiveId] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const accordionRefs = useRef({});

  const clickedAccordionRef = useRef(null);
  const accordionTopRef = useRef(0);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Listen to scroll to toggle the back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowScrollTop(currentScrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSidebarClick = (id) => {
    setActiveId(id);
    setOpenAccordionId(id);

    setTimeout(() => {
      const element = accordionRefs.current[id];
      if (element) {
        const yOffset = -120; // Offset to account for fixed header
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const toggleAccordion = (id) => {
    clickedAccordionRef.current = id;
    const element = accordionRefs.current[id];
    if (element) {
      accordionTopRef.current = element.getBoundingClientRect().top;
    }
    setOpenAccordionId(openAccordionId === id ? null : id);
  };

  useLayoutEffect(() => {
    if (!clickedAccordionRef.current) return;
    const element = accordionRefs.current[clickedAccordionRef.current];
    if (!element) return;
    const newTop = element.getBoundingClientRect().top;
    window.scrollBy(0, newTop - accordionTopRef.current);
  }, [openAccordionId]);

  if (!data) return null;

  return (
    <div className="policy-container">
      <aside className="policy-sidebar">
        <div className="contents-title">CONTENTS</div>
        <div className="policy-nav-list">
          {data.sections && data.sections.map((item) => (
            <div
              key={item.id}
              className={`policy-nav-item ${activeId === item.id ? "active" : ""}`}
              onClick={() => handleSidebarClick(item.id)}
            >
              <span className="nav-num">
                {item.article ? item.article.replace(/^Article\s+/i, "") : String(item.id).padStart(2, "0")}
              </span>
              <span className="content-title">{item.title}</span>
            </div>
          ))}
        </div>

        <div className="policy-nav-select-container">
          <div
            className="policy-mobile-header"
            onClick={() => setShowMenu(!showMenu)}
          >
            <span>CONTENTS</span>
            {showMenu ? <FiChevronUp /> : <FiChevronDown />}
          </div>

          {showMenu && (
            <div className="policy-mobile-list">
              {data.sections && data.sections.map((item) => (
                <div
                  key={item.id}
                  className="policy-nav-item"
                  onClick={() => {
                    handleSidebarClick(item.id);
                    setShowMenu(false);
                  }}
                >
                  {item.article ? `${item.article}: ` : ""}{item.title}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      <main className="policy-main">
        <h1>{data.documentTitle || data.title}</h1>
        
        {(data.lastUpdated || data.companyName || data.version) && (
          <div className="policy-meta-row">
            {data.lastUpdated && (
              <div className="policy-date">
                <FiCalendar color="#2563EB" /> Last Updated: {data.lastUpdated}
              </div>
            )}
            {data.companyName && (
              <span className="policy-version-badge">{data.companyName}</span>
            )}
            {data.version && (
              <span className="policy-version-badge">{data.version}</span>
            )}
          </div>
        )}

        {data.importantNotice && (
          <div className="policy-notice-box">
            <div className="policy-notice-badge">IMPORTANT NOTICE</div>
            <p>{parseInlineFormatting(data.importantNotice)}</p>
          </div>
        )}

        {(data.introduction || data.intro) && (
          <div className="policy-intro">
            {data.introduction && (
              <RenderFormattedContent content={data.introduction} />
            )}
            
            {data.intro && typeof data.intro === "string" && (
              <RenderFormattedContent content={data.intro} />
            )}

            {data.intro && data.intro.topText && (
              <RenderFormattedContent content={data.intro.topText} />
            )}

            {data.intro && data.intro.list && data.intro.list.length > 0 && (
              <ul className="intro-list">
                {data.intro.list.map((item, index) => (
                  <li key={index} className="intro-list-item">
                    <span className="intro-list-heading">{parseInlineFormatting(item.heading)}</span>
                    {" – "}
                    <span className="intro-list-content">{parseInlineFormatting(item.content)}</span>
                  </li>
                ))}
              </ul>
            )}

            {data.intro && data.intro.bottomText && (
              <RenderFormattedContent content={data.intro.bottomText} />
            )}
          </div>
        )}

        <div className="policy-accordions">
          {data.sections && data.sections.map((item) => {
            const isOpen = openAccordionId === item.id;
            return (
              <div
                key={item.id}
                className={`accordion-item ${isOpen ? "open" : ""}`}
                ref={(el) => (accordionRefs.current[item.id] = el)}
              >
                <div
                  className="accordion-header"
                  onClick={() => toggleAccordion(item.id)}
                >
                  <div className="accordion-title-container">
                    <span className="accordion-num">
                      {item.article || String(item.id).padStart(2, "0")}
                    </span>
                    <span className="accordion-title">{item.title}</span>
                  </div>
                  <div className="accordion-icon">
                    <FiChevronDown />
                  </div>
                </div>
                {isOpen && (
                  <div className="accordion-content">
                    {item.content && (
                      <RenderFormattedContent content={item.content} />
                    )}
                    {item.intro && (
                      <RenderFormattedContent content={item.intro} />
                    )}
                    {item.subsections && item.subsections.length > 0 && (
                      <div className="subsections">
                        {item.subsections.map((sub, idx) => (
                          <div key={idx} className="subsection">
                            {sub.title && (
                              <h4 className="subsection-title">
                                {sub.id && <span className="subsection-badge">§ {sub.id}</span>}
                                {parseInlineFormatting(sub.title)}
                              </h4>
                            )}
                            {sub.intro && (
                              <p className="policy-paragraph">{parseInlineFormatting(sub.intro)}</p>
                            )}
                            {sub.content && (
                              <RenderFormattedContent content={sub.content} />
                            )}
                            {sub.definitions && (
                              <RenderDefinitions definitions={sub.definitions} />
                            )}
                            {sub.items && (
                              <RenderListItems items={sub.items} />
                            )}
                            {sub.groups && (
                              <RenderGroups groups={sub.groups} />
                            )}
                            {sub.subgroups && (
                              <RenderSubgroups subgroups={sub.subgroups} />
                            )}
                            {sub.note && (
                              <div className="policy-note-box">
                                <div className="policy-note-badge">NOTE</div>
                                <p>{parseInlineFormatting(sub.note)}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {data.contact && (
          <div className="policy-contact-box">
            <h4 className="contact-box-title">Contact Us</h4>
            {data.contact.email && (
              <p className="policy-paragraph">
                <strong>Email: </strong>
                <a href={`mailto:${data.contact.email}`}>{data.contact.email}</a>
              </p>
            )}
            {data.contact.mailingAddress && (
              <p className="policy-paragraph">
                <strong>Address: </strong>
                {data.contact.mailingAddress}
              </p>
            )}
          </div>
        )}
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top-btn" onClick={scrollToTop} aria-label="Go to top">
          <FiChevronUp className="scroll-icon" />
        </button>
      )}
    </div>
  );
}

